// LocationAutocomplete — drop-in replacement for any <input> that holds a
// location string. Suggestions come from the 3-layer place memory backend:
//   1. Family recents  (top)
//   2. Global Kinly cache
//   3. Google Places (paid tiers only, per-user-per-month quota gated)
//      OR Photon/Nominatim (OSM) when Google isn't eligible.
//
// Session-token billing:
//   • Generate one UUID v4 per "search session" (input gains focus while empty).
//   • Pass it on every autocomplete keystroke.
//   • On Google pick → POST /places/details with the SAME token. Closes the
//     billing session at $0.00283/session (vs $2.83/1000 per-request).
//   • Reset the token after a pick or after 3-min idle.
//
// Props:
//   value, onChange(name, {lat, lng}?) — controlled like a normal text input
//   placeholder, autoFocus, testId — passthrough
//   disabled                       — passthrough
import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const DEBOUNCE_MS = 350;
const MIN_QUERY_LEN = 2;
const SESSION_IDLE_MS = 3 * 60 * 1000; // 3 minutes

// UUID v4 generator. crypto.randomUUID is universally available in modern
// browsers + iOS WKWebView 15+; fall back to a non-cryptographic shuffle if
// running in an ancient runtime so we never crash.
function uuidv4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function LocationAutocomplete({
  value,
  onChange,
  placeholder = "Add a location",
  autoFocus = false,
  testId = "location-autocomplete",
  disabled = false,
  className = "",
}) {
  const { family } = useAuth();
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const rootRef = useRef(null);
  // Session token + last-activity stamp. The token survives across many
  // keystrokes within a single search; we rotate it after a pick OR after
  // SESSION_IDLE_MS of no activity.
  const sessionRef = useRef({ token: null, lastTouchAt: 0 });

  const getSessionToken = useCallback(() => {
    const now = Date.now();
    const s = sessionRef.current;
    if (!s.token || now - s.lastTouchAt > SESSION_IDLE_MS) {
      s.token = uuidv4();
    }
    s.lastTouchAt = now;
    return s.token;
  }, []);

  const resetSession = useCallback(() => {
    sessionRef.current = { token: null, lastTouchAt: 0 };
  }, []);

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const search = (q) => {
    if (!family?.id || !q?.trim() || q.trim().length < MIN_QUERY_LEN) {
      setResults([]); setOpen(false); return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/families/${family.id}/places/autocomplete`,
          { params: { q: q.trim(), session_token: getSessionToken() } },
        );
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
  };

  // Close the dropdown when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const pick = async (item) => {
    setOpen(false);
    let finalName = item.name;
    let finalLat = item.lat;
    let finalLng = item.lng;
    let placeId = item.place_id;

    // Google suggestions ship without lat/lng to keep autocomplete cheap.
    // Resolve them now via /places/details — this is the call that closes
    // the billing session and yields the cached/Google lat,lng,address.
    if (item.source === "google" && item.place_id) {
      try {
        const { data } = await api.post(
          `/families/${family.id}/places/details`,
          { place_id: item.place_id, session_token: getSessionToken() },
        );
        finalName = data.name || finalName;
        finalLat = data.lat;
        finalLng = data.lng;
      } catch {
        // Details lookup failed — keep the name but ship without coords.
      }
    }

    onChange(finalName, finalLat != null && finalLng != null ? { lat: finalLat, lng: finalLng } : undefined);
    // Pick "closes" the session — next focus starts fresh.
    resetSession();

    // Boost both layers' hit-counts asynchronously.
    if (family?.id) {
      api.post(`/families/${family.id}/places/confirm`, {
        name: finalName,
        lat: finalLat,
        lng: finalLng,
        osm_id: item.osm_id,
        place_id: placeId,
      }).catch(() => { /* best-effort */ });
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <div className="flex items-center gap-2 bg-secondary rounded-xl px-3 h-12">
        <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          data-testid={testId}
          className="flex-1 bg-transparent text-base outline-none disabled:opacity-50"
        />
        {loading && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
      </div>

      {open && results.length > 0 && (
        <ul
          data-testid={`${testId}-suggestions`}
          className="absolute left-0 right-0 top-full mt-1 z-50 max-h-72 overflow-y-auto rounded-xl bg-card border border-border shadow-lg py-1"
        >
          {results.map((r, i) => (
            <li key={`${r.source}-${r.name}-${i}`}>
              <button
                type="button"
                data-testid={`${testId}-suggestion-${i}`}
                onClick={() => pick(r)}
                className="w-full flex items-start gap-2 px-3 py-2 text-left tap hover:bg-secondary/60 transition-colors"
              >
                <MapPin
                  className={[
                    "w-3.5 h-3.5 mt-1 shrink-0",
                    r.source === "family" ? "text-primary" :
                    r.source === "google" ? "text-foreground" :
                    r.source === "global" ? "text-muted-foreground" :
                    "text-muted-foreground/70",
                  ].join(" ")}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{r.name}</span>
                  <span className="block text-[10px] uppercase tracking-wider text-muted-foreground">
                    {r.source === "family" ? "Recent" :
                     r.source === "global" ? "Popular with families" :
                     r.source === "google" ? "Google Places" :
                     "Map result"}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
